/**
 * EffectsPresetSaveDialog Component
 * 
 * Modal dialog for saving effects chain presets.
 * Allows users to name, describe, and categorize their presets.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { effectsPresetService, type InstrumentCategory } from '../../services/effects/EffectsPresetService';
import type { EffectInstance } from '../../services/effects/EffectsManager';
import { announceToScreenReader, trapFocus } from '../../utils/accessibility';
import './EffectsPresetSaveDialog.css';

/**
 * Props for EffectsPresetSaveDialog component
 */
export interface EffectsPresetSaveDialogProps {
  /** Whether the dialog is open */
  isOpen: boolean;
  /** Callback to close the dialog */
  onClose: () => void;
  /** Current effects chain to save */
  effects: EffectInstance[];
  /** Callback when preset is saved successfully */
  onSaved?: (presetId: string) => void;
}

/**
 * Available instrument categories
 */
const CATEGORIES: InstrumentCategory[] = [
  'Lead',
  'Bass',
  'Pad',
  'Arp',
  'Percussion',
  'SFX',
  'General',
];

/**
 * EffectsPresetSaveDialog component
 * 
 * Renders a modal dialog with:
 * - Preset name input (required)
 * - Description textarea (optional)
 * - Category selector (optional)
 * - Save and cancel buttons
 * 
 * @param props - Component props
 * @returns EffectsPresetSaveDialog component
 */
export function EffectsPresetSaveDialog({
  isOpen,
  onClose,
  effects,
  onSaved,
}: EffectsPresetSaveDialogProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<InstrumentCategory>('General');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const dialogRef = useRef<HTMLDivElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);

  /**
   * Reset form when dialog opens
   */
  useEffect(() => {
    if (isOpen) {
      setName('');
      setDescription('');
      setCategory('General');
      setError(null);
      
      // Focus name input
      setTimeout(() => {
        nameInputRef.current?.focus();
      }, 100);
      
      // Announce dialog opening
      announceToScreenReader(
        'Save effects preset dialog opened. Enter a name for your preset.',
        'polite'
      );
      
      // Trap focus within dialog
      if (dialogRef.current) {
        const cleanup = trapFocus(dialogRef.current);
        return cleanup;
      }
    }
    return undefined;
  }, [isOpen]);

  /**
   * Handle escape key to close dialog
   */
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && !isSaving) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
    return undefined;
  }, [isOpen, isSaving, onClose]);

  /**
   * Handle click outside dialog to close
   */
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dialogRef.current && !dialogRef.current.contains(e.target as Node) && !isSaving) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
    return undefined;
  }, [isOpen, isSaving, onClose]);

  /**
   * Handle form submission
   */
  const handleSave = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate name
    if (!name.trim()) {
      setError('Please enter a preset name');
      nameInputRef.current?.focus();
      return;
    }

    // Validate effects
    if (effects.length === 0) {
      setError('Cannot save an empty effects chain');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      const preset = await effectsPresetService.savePreset({
        name: name.trim(),
        description: description.trim() || undefined,
        category: category || undefined,
        effects: effects.map(effect => ({ ...effect })), // Deep copy
        isFavorite: false,
        isDefault: false,
      });

      announceToScreenReader(
        `Preset "${preset.name}" saved successfully`,
        'polite'
      );

      // Call onSaved callback
      if (onSaved) {
        onSaved(preset.id);
      }

      // Close dialog
      onClose();
    } catch (err) {
      console.error('Failed to save preset:', err);
      setError(err instanceof Error ? err.message : 'Failed to save preset');
      announceToScreenReader(
        'Failed to save preset. Please try again.',
        'assertive'
      );
    } finally {
      setIsSaving(false);
    }
  }, [name, description, category, effects, onSaved, onClose]);

  /**
   * Handle cancel
   */
  const handleCancel = useCallback(() => {
    if (!isSaving) {
      onClose();
    }
  }, [isSaving, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="preset-dialog-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="preset-save-dialog-title"
    >
      <div className="preset-dialog" ref={dialogRef}>
        <div className="preset-dialog-header">
          <h2 id="preset-save-dialog-title" className="preset-dialog-title">
            Save Effects Preset
          </h2>
          <button
            className="preset-dialog-close"
            onClick={handleCancel}
            disabled={isSaving}
            aria-label="Close dialog"
            type="button"
          >
            ✕
          </button>
        </div>

        <form className="preset-dialog-form" onSubmit={handleSave}>
          {/* Error message */}
          {error && (
            <div className="preset-dialog-error" role="alert">
              <i className="fas fa-exclamation-circle" aria-hidden="true"></i>
              <span>{error}</span>
            </div>
          )}

          {/* Preset name */}
          <div className="preset-dialog-field">
            <label htmlFor="preset-name" className="preset-dialog-label">
              Preset Name <span className="preset-dialog-required">*</span>
            </label>
            <input
              ref={nameInputRef}
              id="preset-name"
              type="text"
              className="preset-dialog-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Crunchy 8-bit"
              maxLength={50}
              disabled={isSaving}
              required
              aria-required="true"
            />
            <div className="preset-dialog-hint">
              Give your preset a descriptive name
            </div>
          </div>

          {/* Description */}
          <div className="preset-dialog-field">
            <label htmlFor="preset-description" className="preset-dialog-label">
              Description <span className="preset-dialog-optional">(optional)</span>
            </label>
            <textarea
              id="preset-description"
              className="preset-dialog-textarea"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g., Heavy bit crushing for retro game sounds"
              maxLength={200}
              rows={3}
              disabled={isSaving}
            />
            <div className="preset-dialog-hint">
              Add a brief description to help you remember what this preset does
            </div>
          </div>

          {/* Category */}
          <div className="preset-dialog-field">
            <label htmlFor="preset-category" className="preset-dialog-label">
              Category <span className="preset-dialog-optional">(optional)</span>
            </label>
            <select
              id="preset-category"
              className="preset-dialog-select"
              value={category}
              onChange={(e) => setCategory(e.target.value as InstrumentCategory)}
              disabled={isSaving}
            >
              {CATEGORIES.map(cat => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
            <div className="preset-dialog-hint">
              Organize your presets by instrument type
            </div>
          </div>

          {/* Effects preview */}
          <div className="preset-dialog-field">
            <div className="preset-dialog-label">Effects in this preset</div>
            <div className="preset-dialog-effects-preview">
              {effects.length === 0 ? (
                <div className="preset-dialog-effects-empty">
                  No effects in chain
                </div>
              ) : (
                effects.map((effect, index) => (
                  <div key={effect.id} className="preset-dialog-effect-item">
                    <span className="preset-dialog-effect-number">{index + 1}.</span>
                    <span className="preset-dialog-effect-type">{effect.type}</span>
                    {effect.bypassed && (
                      <span className="preset-dialog-effect-badge">Bypassed</span>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="preset-dialog-actions">
            <button
              type="button"
              className="preset-btn preset-btn-secondary"
              onClick={handleCancel}
              disabled={isSaving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="preset-btn preset-btn-primary"
              disabled={isSaving || !name.trim() || effects.length === 0}
            >
              {isSaving ? (
                <>
                  <i className="fas fa-spinner fa-spin" aria-hidden="true"></i>
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <i className="fas fa-save" aria-hidden="true"></i>
                  <span>Save Preset</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
